import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { Anime } from "@/mocks/anime";

interface HeroCarouselProps {
  items: Anime[];
}

export default function HeroCarousel({ items }: HeroCarouselProps) {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (items.length <= 1) return;
    const timer = setInterval(() => {
      setIndex((prev) => (prev + 1) % items.length);
    }, 5500);
    return () => clearInterval(timer);
  }, [items.length]);

  if (items.length === 0) return null;

  const goTo = (next: number) => {
    setIndex((next + items.length) % items.length);
  };

  return (
    <section className="relative w-full">
      <div className="relative h-[420px] w-full overflow-hidden md:h-[560px]">
        {items.map((item, i) => (
          <div
            key={item.id}
            className={`absolute inset-0 transition-opacity duration-700 ${
              i === index ? "opacity-100" : "opacity-0 pointer-events-none"
            }`}
          >
            <img
              src={item.banner || item.cover}
              alt={`${item.title} 焦点图`}
              title={`${item.title} 在线观看 - 樱花动漫`}
              className="h-full w-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/85 via-foreground-950/50 to-foreground-950/10"></div>
          </div>
        ))}

        <div className="relative z-10 flex h-full w-full items-center px-6 md:px-10 lg:px-16">
          <div className="w-full max-w-2xl" key={items[index].id}>
            <div className="animate-fade-up">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-accent-500 px-3 py-1 text-xs font-medium text-background-50">
                <i className="ri-fire-fill"></i>
                本周热门推荐
              </span>
              <h2 className="mt-4 text-3xl font-bold leading-tight text-background-50 md:text-5xl">
                {items[index].title}
              </h2>
              <div className="mt-3 flex flex-wrap items-center gap-3 text-sm text-background-200">
                <span className="text-accent-300 font-semibold">{items[index].rating.toFixed(1)} 分</span>
                <span>{items[index].year}</span>
                <span>{items[index].region}</span>
                <span>{items[index].genres.join(" / ")}</span>
                <span>{items[index].status}</span>
              </div>
              <p className="mt-4 max-w-xl text-sm leading-relaxed text-background-200 md:text-base">
                {items[index].description}
              </p>
              <div className="mt-6 flex flex-wrap items-center gap-3">
                <Link
                  to={`/detail/${items[index].id}`}
                  className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
                >
                  <i className="ri-play-fill text-lg"></i>
                  立即观看
                </Link>
                <Link
                  to="/category/all"
                  className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-50/40 bg-background-50/10 px-5 py-2.5 text-sm font-medium text-background-50 backdrop-blur transition-colors hover:bg-background-50/20"
                >
                  浏览全集
                </Link>
              </div>
            </div>
          </div>
        </div>

        <button
          type="button"
          aria-label="上一张"
          onClick={() => goTo(index - 1)}
          className="absolute left-3 top-1/2 z-10 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full bg-background-50/20 text-background-50 backdrop-blur transition-colors hover:bg-primary-500 md:left-6 md:h-11 md:w-11 cursor-pointer"
        >
          <i className="ri-arrow-left-s-line text-2xl"></i>
        </button>
        <button
          type="button"
          aria-label="下一张"
          onClick={() => goTo(index + 1)}
          className="absolute right-3 top-1/2 z-10 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full bg-background-50/20 text-background-50 backdrop-blur transition-colors hover:bg-primary-500 md:right-6 md:h-11 md:w-11 cursor-pointer"
        >
          <i className="ri-arrow-right-s-line text-2xl"></i>
        </button>

        <div className="absolute bottom-4 left-1/2 z-10 flex -translate-x-1/2 items-center gap-2 md:bottom-6">
          {items.map((item, i) => (
            <button
              key={item.id}
              type="button"
              aria-label={`切换到 ${item.title}`}
              onClick={() => setIndex(i)}
              className={`h-1.5 rounded-full transition-all cursor-pointer ${
                i === index ? "w-8 bg-primary-500" : "w-3 bg-background-50/50 hover:bg-background-50/80"
              }`}
            ></button>
          ))}
        </div>
      </div>
    </section>
  );
}